<script setup lang="ts">
import {
  GAMES_USE_MOCKS,
  GetGamePrizes,
  GetGamesSummary,
  GetKioskGame,
  GetKioskGamePlays,
  SetKioskGameStatus,
} from "@/api/gamesApi";
import GameFormDialog from "@/components/games/GameFormDialog.vue";
import GamePlaysTable from "@/components/games/GamePlaysTable.vue";
import GamePrizeOddsEditor from "@/components/games/GamePrizeOddsEditor.vue";
import GamesDemoAlert from "@/components/games/GamesDemoAlert.vue";
import GamesKpiStrip from "@/components/games/GamesKpiStrip.vue";
import AppDocumentHeader from "@/components/shared/AppDocumentHeader.vue";
import AppTabs from "@/components/shared/AppTabs.vue";
import { useGamesFormat } from "@/composables/useGamesFormat";
import { GAMES_ADMIN_ROLES } from "@/config/moduleRegistry/keys";
import { canAnyRole, canDoctype } from "@/helper/permissionHelper";
import useToastify from "@/stores/toastify.store";
import type { TabItem } from "@/type/tabs";
import {
  GAME_STATUS_COLORS,
  GAME_TYPE_ICONS,
  SKILL_GAME_TYPES,
  TARGET_GAME_TYPES,
  emptyKioskGame,
  gameStatus,
  type AddEditKioskGame,
  type GamePrize,
  type GamesSummary,
  type KioskGame,
  type KioskGamePlay,
} from "@/type/games";

/**
 * One game campaign: its odds (the thing most often changed), its plays, and its rules.
 * The KPI strip sits above the tabs because it is the context for all three.
 */

definePage({ meta: { permissionKey: "page.games.detail" } });

const route = useRoute();
const { t, locale } = useI18n({ useScope: "global" });
const toast = useToastify();
const {
  gameTitle,
  gameTypeLabel,
  gameStatusLabel,
  prizeTitle,
  formatDate,
  formatInteger,
} = useGamesFormat();

/** Read through a cast: a brand-new route's params are not in the generated union yet. */
function routeParam(value: unknown) {
  return Array.isArray(value) ? String(value[0] ?? "") : String(value ?? "");
}

const gameId = computed(() => decodeURIComponent(routeParam((route.params as Record<string, unknown>).id)));

const game = ref<KioskGame | null>(null);
const prizes = ref<GamePrize[]>([]);
const loading = ref(false);
const failed = ref(false);

const plays = ref<KioskGamePlay[]>([]);
const playsLoading = ref(false);

const summary = ref<GamesSummary | null>(null);
const summaryLoading = ref(false);
const summaryFailed = ref(false);

const canManage = computed(() => GAMES_USE_MOCKS || canDoctype("Kiosk Game", "write") || canAnyRole(GAMES_ADMIN_ROLES));

async function load() {
  failed.value = false;
  try {
    const [g, p] = await Promise.all([GetKioskGame(loading, gameId.value), GetGamePrizes(ref(false))]);
    game.value = g;
    prizes.value = p;
  }
  catch {
    failed.value = true;
  }
  void loadPlays();
  void loadSummary();
}

async function loadPlays() {
  try {
    plays.value = await GetKioskGamePlays(playsLoading, { game: gameId.value });
  }
  catch {
    plays.value = [];
  }
}

async function loadSummary() {
  summaryFailed.value = false;
  try {
    summary.value = await GetGamesSummary(summaryLoading, gameId.value);
  }
  catch {
    summaryFailed.value = true;
  }
}

watch(gameId, load, { immediate: true });

const effectiveStatus = computed(() => (game.value ? gameStatus(game.value) : "draft"));

const otherLanguageTitle = computed(() => {
  if (!game.value)
    return null;

  return locale.value === "ar" ? game.value.title : game.value.title_ar;
});

const breadcrumbs = computed(() => [
  { title: t("alkokh.games.list.title"), to: "/games" },
  { title: gameTitle(game.value), disabled: true },
]);

const headerChips = computed(() => game.value
  ? [
    { key: "type", label: gameTypeLabel(game.value.game_type), icon: GAME_TYPE_ICONS[game.value.game_type] },
    { key: "status", label: gameStatusLabel(effectiveStatus.value), color: GAME_STATUS_COLORS[effectiveStatus.value] },
  ]
  : []);

const activeTab = ref("prizes");
const tabs = computed<TabItem[]>(() => [
  { value: "prizes", labelKey: "alkokh.games.tabs.prizes", icon: "tabler-gift", badge: game.value?.prizes.length || null },
  { value: "plays", labelKey: "alkokh.games.tabs.plays", icon: "tabler-history", badge: plays.value.length || null },
  { value: "settings", labelKey: "alkokh.games.tabs.settings", icon: "tabler-settings" },
]);

// --- Status --------------------------------------------------------------------

const statusBusy = ref(false);

async function toggleStatus() {
  if (!game.value)
    return;
  if (game.value.status !== "active" && !game.value.prizes.length) {
    toast.warning({ text: t("alkokh.games.validation.activeNeedsPrizes") });

    return;
  }
  try {
    const next = game.value.status === "active" ? "paused" : "active";
    game.value = await SetKioskGameStatus(statusBusy, game.value.name, next);
    toast.success({ text: t(next === "active" ? "alkokh.games.messages.activated" : "alkokh.games.messages.paused") });
    void loadSummary();
  }
  catch (error: any) {
    toast.error({ text: error?.message || t("alkokh.games.messages.saveFailed") });
  }
}

/** Why an Active game is not on the kiosk right now, when that is the case. */
const statusNote = computed(() => {
  if (!game.value || game.value.status !== "active")
    return null;
  if (effectiveStatus.value === "scheduled")
    return t("alkokh.games.detail.scheduledNote", { date: formatDate(game.value.start_date) });
  if (effectiveStatus.value === "ended")
    return t("alkokh.games.detail.endedNote", { date: formatDate(game.value.end_date) });

  return null;
});

// --- Edit ----------------------------------------------------------------------

const editOpen = ref(false);
const editForm = ref<AddEditKioskGame>(emptyKioskGame());

function openEdit() {
  if (!game.value)
    return;
  editForm.value = JSON.parse(JSON.stringify(game.value));
  editOpen.value = true;
}

function onSaved(saved: KioskGame) {
  game.value = saved;
  void loadSummary();
}

// --- Settings tab ----------------------------------------------------------------

const settingsRows = computed(() => {
  const g = game.value;
  if (!g)
    return [];
  const isSkill = SKILL_GAME_TYPES.includes(g.game_type);
  const consolation = prizes.value.find(p => p.name === g.consolation_prize);

  return [
    { key: "type", label: t("alkokh.games.gameFields.type"), value: gameTypeLabel(g.game_type) },
    { key: "titleEn", label: t("alkokh.games.gameFields.title"), value: g.title },
    { key: "titleAr", label: t("alkokh.games.gameFields.titleAr"), value: g.title_ar || "—" },
    { key: "start", label: t("alkokh.games.gameFields.startDate"), value: g.start_date ? formatDate(g.start_date) : t("alkokh.games.gameFields.openEnded") },
    { key: "end", label: t("alkokh.games.gameFields.endDate"), value: g.end_date ? formatDate(g.end_date) : t("alkokh.games.gameFields.openEnded") },
    { key: "branches", label: t("alkokh.games.gameFields.branches"), value: g.branches.length ? g.branches.join(locale.value === "ar" ? "، " : ", ") : t("alkokh.games.gameFields.allBranches") },
    { key: "perDay", label: t("alkokh.games.gameFields.playsPerDay"), value: formatInteger(g.plays_per_phone_per_day) },
    ...(isSkill
      ? [
        { key: "time", label: t("alkokh.games.gameFields.timeLimit"), value: `${formatInteger(g.time_limit_seconds)} ${t("alkokh.games.gameFields.seconds")}` },
        ...(TARGET_GAME_TYPES.includes(g.game_type) ? [{ key: "target", label: t("alkokh.games.gameFields.catchTarget"), value: formatInteger(g.target_count) }] : []),
        { key: "consolation", label: t("alkokh.games.gameFields.consolationPrize"), value: consolation ? prizeTitle(consolation) : "—" },
      ]
      : []),
    { key: "notes", label: t("alkokh.games.gameFields.notes"), value: g.notes || "—" },
  ];
});
</script>

<template>
  <div class="game-detail">
    <VProgressLinear
      v-if="loading && !game"
      indeterminate
      color="primary"
    />

    <VAlert
      v-else-if="failed"
      type="error"
      variant="tonal"
      class="mb-4"
    >
      {{ t("alkokh.games.detail.notFound") }}
      <template #append>
        <VBtn
          size="small"
          variant="text"
          to="/games"
        >
          {{ t("alkokh.games.detail.backToList") }}
        </VBtn>
      </template>
    </VAlert>

    <template v-else-if="game">
      <AppDocumentHeader
        :icon="GAME_TYPE_ICONS[game.game_type]"
        :title="gameTitle(game)"
        :subtitle="otherLanguageTitle || gameTypeLabel(game.game_type)"
        :breadcrumbs="breadcrumbs"
        :chips="headerChips"
      >
        <template #actions>
          <template v-if="canManage">
            <VBtn
              variant="tonal"
              prepend-icon="tabler-pencil"
              @click="openEdit"
            >
              {{ t("alkokh.global.edit") }}
            </VBtn>
            <VBtn
              :color="game.status === 'active' ? 'warning' : 'success'"
              :prepend-icon="game.status === 'active' ? 'tabler-player-pause' : 'tabler-player-play'"
              :loading="statusBusy"
              class="ms-2"
              @click="toggleStatus"
            >
              {{ game.status === "active" ? t("alkokh.games.actions.pause") : t("alkokh.games.actions.activate") }}
            </VBtn>
          </template>
        </template>
      </AppDocumentHeader>

      <div class="mt-4">
        <GamesDemoAlert @reset="load" />
      </div>

      <VAlert
        v-if="statusNote"
        type="info"
        variant="tonal"
        density="compact"
        icon="tabler-calendar"
        class="mb-4"
      >
        {{ statusNote }}
      </VAlert>

      <GamesKpiStrip
        class="mb-4"
        :summary="summary"
        :loading="summaryLoading"
        :failed="summaryFailed"
        hide-active-games
      />

      <AppTabs
        v-model="activeTab"
        :items="tabs"
        sync-mode="query"
        sync-key="tab"
      >
        <template #panel-prizes>
          <GamePrizeOddsEditor
            :game="game"
            :prizes="prizes"
            :can-edit="canManage"
            @saved="onSaved"
          />
        </template>

        <template #panel-plays>
          <VCard
            elevation="0"
            border
          >
            <VCardText>
              <GamePlaysTable
                :rows="plays"
                :loading="playsLoading"
                hide-game
              />
            </VCardText>
          </VCard>
        </template>

        <template #panel-settings>
          <VCard
            elevation="0"
            border
          >
            <VCardText>
              <dl class="game-detail__settings">
                <template
                  v-for="row in settingsRows"
                  :key="row.key"
                >
                  <dt>{{ row.label }}</dt>
                  <dd>{{ row.value }}</dd>
                </template>
              </dl>
              <VBtn
                v-if="canManage"
                variant="tonal"
                prepend-icon="tabler-pencil"
                class="mt-4"
                @click="openEdit"
              >
                {{ t("alkokh.global.edit") }}
              </VBtn>
            </VCardText>
          </VCard>
        </template>
      </AppTabs>

      <GameFormDialog
        v-model="editOpen"
        v-model:game="editForm"
        :prizes="prizes"
        :original="game"
        @saved="onSaved"
      />
    </template>
  </div>
</template>

<style scoped>
.game-detail__settings {
  display: grid;
  grid-template-columns: minmax(140px, max-content) 1fr;
  gap: 10px 24px;
  margin: 0;
}

.game-detail__settings dt {
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.game-detail__settings dd {
  margin: 0;
  font-weight: 500;
}

@media (max-width: 599px) {
  .game-detail__settings {
    grid-template-columns: 1fr;
    gap: 2px;
  }

  .game-detail__settings dd {
    margin-block-end: 10px;
  }
}
</style>
